// AppLayout.js — Wave 1 shell.
//
// Structure: header (brand → nav → bell → identity) → page content.
// Changes from the legacy shell:
//   - the notification bell is part of the header (no separate sticky white
//     strip, no magic `top-[64px]` offset);
//   - role display/gating prefers server-confirmed identity via useMyIdentity()
//     (M3) and only falls back to the session cache for the anonymous state;
//   - deliberate mobile drawer navigation (<=1023px) instead of squeezed
//     desktop nav;
//   - skip-link + semantic landmarks for accessibility.
// Page routes/permissions are unchanged; this is presentation only.

import React, { useEffect, useState } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import Navbar from "./Navbar";
import A11yToggleBar from "../components/A11yToggleBar";
import NotificationBell from "../NotificationContext/NotificationBell";
import { useMyIdentity } from "../utils/identity";
import { getSessionLandingRoute } from "../utils/dashboardRoute";
import "../styles/shell.css";

// Full-bleed pages (home keeps its own hero background).
const FULL_BLEED_PATHS = ["/"];

const formatIdentityHeader = (identity) => {
  const role = String(identity?.role || "USER").trim();
  const unit = String(identity?.unit || "").trim();
  const branch = String(identity?.branch || "").trim();
  const subBranch = String(identity?.subBranch || identity?.unitBranch || "").trim();
  const pathSegments = [
    unit,
    [branch, subBranch].filter(Boolean).join("/"),
  ].filter(Boolean);

  return {
    roleLabel: role ? `# ${role.toUpperCase()}` : "# USER",
    pathLabel: pathSegments.length ? pathSegments.join(" / ") : "KA/HQ KA / OPS BRANCH/CIS SUBBRANCH",
  };
};

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname || "";
  const isFullBleed = FULL_BLEED_PATHS.includes(path);

  // The Navbar owns the public/auth-path rules (bell hidden on /, /login,
  // /register, /admin) so shell behaviour stays consistent in one place.
  const bellVisible = Navbar.isBellVisibleForPath(path);

  const { identity, resolved } = useMyIdentity();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer whenever navigation happens.
  useEffect(() => {
    setDrawerOpen(false);
  }, [path]);

  const hasSessionToken = Boolean(
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || localStorage.getItem("adminToken")
  );
  const isAuthed = Boolean(identity) || (resolved === false && hasSessionToken);

  const homeRoute = getSessionLandingRoute(identity);
  const navItems = Navbar.getNavItems({ isLoggedIn: isAuthed, identity, homeRoute });
  const identityHeader = formatIdentityHeader(identity);

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className="wv-shell">
      <a className="wv-skip-link" href="#wv-main-content">
        Skip to main content
      </a>

      <header className="wv-header">
        <Navbar mode="brand" />

        {/* Desktop navigation (inside the same header) */}
        <nav className="wv-header__nav" aria-label="Primary">
          {navItems.map((item) => (
            <button
              key={item.to}
              type="button"
              className={`wv-header__btn ${item.variant === "primary" ? "wv-header__btn--primary" : ""}`.trim()}
              aria-current={path === item.to ? "page" : undefined}
              onClick={() => navigate(item.to)}
            >
              {item.icon ? <item.icon size={15} aria-hidden="true" /> : null}
              {item.label}
            </button>
          ))}
        </nav>

        <div className="wv-header__spacer" />

        {bellVisible && isAuthed ? (
          <div className="wv-header__bell">
            <NotificationBell />
          </div>
        ) : null}

        {isAuthed && identity ? (
          <div className="wv-header__identity">
            <span className="wv-header__identity-role">{identityHeader.roleLabel}</span>
            <span className="wv-header__identity-path">{identityHeader.pathLabel}</span>
          </div>
        ) : null}

        {/* Desktop Login/Logout button — same control as the drawer footer */}
        <div className="wv-header__auth">
          <Navbar mode="auth" />
        </div>

        {/* Low-vision accessibility toggles (persisted per browser) */}
        <A11yToggleBar />

        {/* Mobile hamburger — deliberate drawer, not squeezed desktop nav */}
        {isAuthed || navItems.length > 0 ? (
          <button
            type="button"
            className="wv-header__btn wv-header__menu-btn"
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls="wv-mobile-drawer"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={18} aria-hidden="true" />
          </button>
        ) : null}
      </header>

      {drawerOpen ? (
        <>
          <div className="wv-drawer-backdrop" onClick={closeDrawer} aria-hidden="true" />
          <aside
            id="wv-mobile-drawer"
            className="wv-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="wv-drawer__header">
              <div>
                <div className="wv-drawer__identity-name">
                  {identity?.name || identity?.username || "WAVY"}
                </div>
                <div className="wv-drawer__identity-role">{identity ? formatIdentityHeader(identity).roleLabel : "Not signed in"}</div>
                {identity ? <div className="wv-drawer__identity-path">{formatIdentityHeader(identity).pathLabel}</div> : null}
              </div>
              <button type="button" className="wv-icon-btn" aria-label="Close menu" onClick={closeDrawer}>
                <Menu size={18} style={{ transform: "rotate(90deg)" }} aria-hidden="true" />
              </button>
            </div>
            <nav className="wv-drawer__nav" aria-label="Mobile navigation">
              {navItems.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  className="wv-header__btn"
                  aria-current={path === item.to ? "page" : undefined}
                  onClick={() => {
                    closeDrawer();
                    navigate(item.to);
                  }}
                >
                  {item.icon ? <item.icon size={16} aria-hidden="true" /> : null}
                  {item.label}
                </button>
              ))}
              {bellVisible && isAuthed ? (
                <div style={{ padding: "var(--wv-space-2) 0" }}>
                  <NotificationBell />
                </div>
              ) : null}
            </nav>
            <div className="wv-drawer__footer">
              <div className="wv-drawer__accessibility">
                <span>Display settings</span>
                <A11yToggleBar />
              </div>
              <Navbar mode="auth" onNavigate={closeDrawer} />
            </div>
          </aside>
        </>
      ) : null}

      <main
        id="wv-main-content"
        className={`wv-shell__main ${isFullBleed ? "wv-shell__main--full" : ""}`.trim()}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;
